import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CardSkeleton } from '../../components/common/Skeleton';
import { MapPin, Phone, Mail, Navigation, ShieldCheck, Clock, ArrowRight, MessageCircle } from 'lucide-react';
import { getGelanggangDB, getMembersDB } from '../../services/db';
import { GelanggangDetail, UserProfile } from '../../types';
import { formatPublicName, getGoogleMapsUrl, getGelanggangDisplayAddress } from '../../utils/formatters';

export const ContactPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [gelanggangs, setGelanggangs] = useState<GelanggangDetail[]>([]);
  const [members, setMembers] = useState<UserProfile[]>([]);

  const loadData = () => {
    // Read from persistent cache first for instant sub-second render
    const cachedG = localStorage.getItem('asmm_db_gelanggang_v5');
    const cachedM = localStorage.getItem('asmm_db_members_v4');

    if (cachedG && cachedM) {
      setGelanggangs(JSON.parse(cachedG));
      setMembers(JSON.parse(cachedM));
      setIsLoading(false);
    }

    // Silent background revalidation with Turso DB
    Promise.all([getGelanggangDB(), getMembersDB()]).then(([gData, mData]) => {
      setGelanggangs(gData);
      setMembers(mData);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    loadData();
    const handleDbUpdate = () => loadData();
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleDbUpdate);
  }, []);

  const defaultPhones: Record<string, string> = {
    'gel-pusaka': '013-5819201',
    'gel-wadi': '0124475157',
    'gel-paya': '011-28194012'
  };

  const getLeaderPhone = (g: GelanggangDetail): string => {
    const matchedMember = members.find(
      (m) =>
        m.name.trim().toLowerCase() === g.leaderName.trim().toLowerCase() ||
        (m.role === 'Ketua Gelanggang' && m.gelanggang === g.name)
    );

    if (matchedMember?.phone) {
      return matchedMember.phone;
    }
    if (g.leaderPhone) {
      return g.leaderPhone;
    }
    return defaultPhones[g.id] || '0124475157';
  };

  const setiausahaMember = members.find((m) => m.role === 'Setiausaha' && m.name.toLowerCase().includes('fauzi')) || members.find((m) => m.name.toLowerCase().includes('fauzi'));
  const setiausahaPhone = setiausahaMember?.phone || '012-447 5157';
  const setiausahaName = setiausahaMember ? formatPublicName(setiausahaMember.name) : 'Mohd Fauzi';

  const activeGelanggangs = gelanggangs.filter(g => g.status === 'Aktif');

  return (
    <div className="w-full space-y-8 min-h-[75vh] bg-white text-slate-900 pb-16">
      {/* Hero Overview Centerpiece Card with Overlapping Logo Anchor */}
      <div className="relative pt-10 sm:pt-12 mb-4">
        {/* Overlapping Focal Logo Anchor */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white shadow-md bg-white p-2.5 flex items-center justify-center">
            <img
              src="/logo-pssasmm.png"
              alt="Logo PSSASMM"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-10 pt-14 sm:pt-16 shadow-sm text-center relative">
          <div className="max-w-2xl mx-auto space-y-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold border border-red-100">
              <ShieldCheck className="w-3.5 h-3.5 text-red-600" />
              <span>Pendaftaran Gelanggang Rasmi & Khidmat Ahli</span>
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Gelanggang Latihan Seni Silat ASMM
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
              Pilih gelanggang latihan berdekatan anda untuk sesi bimbingan seni silat tradisi, pendaftaran ahli baru, atau hubungi setiausaha induk untuk sebarang urusan pentadbiran pertubuhan.
            </p>
          </div>

          {/* Floating Metric Pill Bar */}
          <div className="bg-white rounded-3xl sm:rounded-full py-3.5 px-6 sm:px-10 shadow-sm border border-slate-200/80 flex flex-wrap items-center justify-around gap-4 sm:gap-8 max-w-2xl mx-auto mt-8">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-slate-900">{activeGelanggangs.length || 5}</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Gelanggang Aktif</div>
            </div>
            <div className="hidden sm:block h-7 w-px bg-slate-200" />
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-amber-600">Kulim & Lunas</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Kawasan Kedah</div>
            </div>
            <div className="hidden sm:block h-7 w-px bg-slate-200" />
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-red-600">Terbuka</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Pendaftaran Pesilat</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Gelanggang Directory */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-sm space-y-5">
            <div>
              <h3 className="text-lg font-black text-slate-900">Senarai Gelanggang Latihan Aktif</h3>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">Hubungi Ketua Gelanggang untuk pendaftaran diri atau anak-anak.</p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {isLoading ? (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              ) : (
                activeGelanggangs.map((g) => {
                  const phone = getLeaderPhone(g);
                  const mapsUrl = getGoogleMapsUrl(g);

                  let cleanPhone = phone.replace(/[^0-9]/g, '');
                  if (cleanPhone.startsWith('0')) {
                    cleanPhone = '6' + cleanPhone;
                  } else if (!cleanPhone.startsWith('60') && cleanPhone.startsWith('1')) {
                    cleanPhone = '60' + cleanPhone;
                  }
                  const waText = encodeURIComponent(
                    `Salam sejahtera, saya berminat untuk mendaftar anak / diri bagi latihan silat di gelanggang *${g.name}*.`
                  );

                  return (
                    <div
                      key={g.id}
                      className="p-5 bg-white border border-slate-100 rounded-2xl shadow-2xs hover:shadow-md transition-all duration-300 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
                    >
                      <div className="space-y-2.5 min-w-0 flex-1">
                        <div className="flex items-center gap-2.5">
                          <span className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100">
                            <MapPin className="w-4 h-4" />
                          </span>
                          <h4 className="font-extrabold text-sm text-slate-900">{g.name}</h4>
                          <span className="text-[10px] bg-red-50 text-red-700 font-bold px-2.5 py-0.5 rounded-full border border-red-100">
                            {g.status}
                          </span>
                        </div>

                        {/* Address & Google Maps link */}
                        <div className="pl-10 space-y-1">
                          <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-slate-600 hover:text-red-600 font-medium leading-relaxed group/addr"
                            title="Klik untuk navigasi Google Maps"
                          >
                            <span className="group-hover/addr:underline">{getGelanggangDisplayAddress(g)}</span>
                          </a>
                          <div className="text-[11px] text-slate-500 font-medium flex items-center gap-1.5 pt-0.5">
                            <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>Jadual: <strong className="text-slate-800">{g.sessionTime}</strong></span>
                          </div>
                        </div>

                        {/* Navigation Badge Button */}
                        <div className="pl-10 pt-1">
                          <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 hover:text-blue-800 bg-blue-50 border border-blue-200/80 px-3 py-1.5 rounded-full transition-all group/nav"
                          >
                            <Navigation className="w-3.5 h-3.5 text-blue-600 group-hover/nav:scale-110 transition-transform shrink-0" />
                            <span>Buka Google Maps</span>
                          </a>
                        </div>
                      </div>

                      <div className="sm:text-right pl-10 sm:pl-0 space-y-2 shrink-0 border-t sm:border-t-0 pt-3 sm:pt-0 border-slate-100">
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Ketua Gelanggang:</div>
                          <div className="text-xs font-black text-slate-900 mt-0.5">{formatPublicName(g.leaderName)}</div>
                        </div>

                        <div>
                          <a
                            href={`https://wa.me/${cleanPhone}?text=${waText}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all cursor-pointer"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>WhatsApp Jurulatih</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Col: HQ & Admin info */}
        <div className="space-y-4">
          <div className="bg-white p-6 sm:p-7 rounded-3xl border border-slate-100 shadow-sm space-y-5">
            <div>
              <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest block">
                Pentadbiran Pusat
              </span>
              <h3 className="text-base font-black text-slate-900 mt-0.5">
                Ibu Pejabat ASMM
              </h3>
            </div>

            <div className="space-y-4 text-xs text-slate-600 font-medium">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100 mt-0.5">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Alamat Surat-Menyurat</h4>
                  <p className="mt-1 leading-relaxed text-slate-600">263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah Darul Aman</p>
                </div>
              </div>

              <div className="flex items-start gap-3 border-t border-slate-100 pt-4">
                <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100 mt-0.5">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Hubungi Setiausaha</h4>
                  <a
                    href={`https://wa.me/6${setiausahaPhone.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 font-mono text-red-600 font-extrabold block hover:underline"
                  >
                    {setiausahaPhone} ({setiausahaName})
                  </a>
                  <p className="text-[11px] text-slate-400 mt-0.5">Urusan keahlian, sijil bengkung & surat rasmi</p>
                </div>
              </div>

              <div className="flex items-start gap-3 border-t border-slate-100 pt-4">
                <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100 mt-0.5">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Emel Rasmi</h4>
                  <p className="mt-1 font-mono text-slate-800 font-bold">silatasmm@gmail.com</p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 text-center space-y-1">
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">No. Pendaftaran ROS (Kedah)</div>
                  <div className="text-xs font-mono font-black text-slate-900">PPM-007-02-28082022</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
